"use client";

import * as React from "react";
import { cn } from "@marketing-sites/utils/cn";
import { NavigationMenu } from "@base-ui/react/navigation-menu";
import { useHeaderContext } from "../../context";
import { DesktopNavItem } from "./desktop-nav-item";
import { SwitchHoverCard } from "./switch-hover-card";
import {
  navFg,
  navPanelBg,
  navPanelBorder,
  navDivider,
} from "../floating-nav-classes";
import { Logo } from "../logo";
import { headerId } from "../../constants";

export function DesktopNav({
  className,
  logoMetadata,
  switchHoverCardMetadata,
}: {
  className?: string;
  logoMetadata: { analyticsId: string };
  switchHoverCardMetadata: { analyticsId: string };
}) {
  const { fixtureData, disableLogoLink, variant } = useHeaderContext();
  const floating = variant === "floating";
  const [activeValue, setActiveValue] = React.useState<string | null>(null);

  React.useEffect(() => {
    const handleNavItemClick = () => setActiveValue(null);
    window.addEventListener("navItemClick", handleNavItemClick);
    return () => window.removeEventListener("navItemClick", handleNavItemClick);
  }, []);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  // Floating variant: portal dropdowns inside the header (brand font/token
  // scope) rather than the document body, so their content keeps the brand
  // typeface. Default variant portals to body as before.
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null);
  React.useEffect(() => {
    if (!floating) return;
    setPortalContainer(document.getElementById(headerId));
  }, [floating]);

  const activeItem = fixtureData.primary.find(
    (item) => "label" in item && item.label === activeValue,
  );
  const dropdownAlign =
    activeItem?.type === "dropdown" ? activeItem.dropdownAlign : undefined;
  const dropdownClassName =
    activeItem?.type === "dropdown" ? activeItem.dropdownClassName : undefined;
  const align = dropdownAlign ?? "start";
  const anchor = align === "start" ? anchorRef.current : undefined;

  return (
    <NavigationMenu.Root
      value={activeValue}
      delay={0}
      onValueChange={setActiveValue}
    >
      <div
        ref={anchorRef}
        className={cn(
          "mx-auto flex w-full max-w-[1920px] items-center gap-2.5",
          className,
        )}
      >
        {/* LOGO */}
        <Logo
          analyticsId={logoMetadata.analyticsId}
          asLink={!disableLogoLink}
          icon={fixtureData.logo.icon}
          href={fixtureData.logo.href}
          className={cn(
            floating && "flex items-center px-1",
            floating && (fixtureData.logo.rowClassName ?? "h-10"),
            !disableLogoLink &&
              "hover:text-nav-content-secondary transition-colors duration-200",
          )}
          label={fixtureData.logo.label}
          analyticsText={fixtureData.logo.analyticsText}
        />

        {/* PRIMARY NAVIGATION */}
        <NavigationMenu.List className="flex">
          {fixtureData.primary.map((item, index) => (
            <DesktopNavItem
              key={
                item.type === "custom" || item.type === "customWithDropdown"
                  ? `custom-${index}`
                  : item.label
              }
              item={item}
            />
          ))}
        </NavigationMenu.List>

        {/* SECONDARY NAVIGATION */}
        <NavigationMenu.List className="ml-auto flex items-center">
          {fixtureData.secondary
            .filter((item) => !(item.type === "button" && item.mobileOnly))
            .map((item, index) => (
              <DesktopNavItem
                key={
                  item.type === "custom" || item.type === "customWithDropdown"
                    ? `custom-${index}`
                    : item.label
                }
                item={item}
                dataTestId="nav-cta"
              />
            ))}
        </NavigationMenu.List>

        {/* CROSS-SITE LINK */}
        {fixtureData.crossSiteLink &&
          !disableLogoLink &&
          (floating && fixtureData.crossSiteCard ? (
            <div className="group/switch relative flex items-center gap-[9px]">
              <div className={cn(navDivider, "h-[33px] w-px")} />
              <ul className="flex">
                <DesktopNavItem item={fixtureData.crossSiteLink} />
              </ul>
              <SwitchHoverCard
                card={fixtureData.crossSiteCard}
                analyticsId={switchHoverCardMetadata.analyticsId}
              />
            </div>
          ) : (
            <ul className="border-nav-border/30 flex border-l pl-2.5">
              <DesktopNavItem item={fixtureData.crossSiteLink} />
            </ul>
          ))}

        {/* SUBMENU PORTAL */}
        <NavigationMenu.Portal
          className="shared-header-portal z-nav-menu relative"
          keepMounted={true}
          container={floating ? portalContainer : undefined}
        >
          {!floating && (
            <NavigationMenu.Backdrop
              className={cn(
                "fixed inset-0 bg-black/20 backdrop-blur-sm",
                "transition-opacity duration-200",
                "data-closed:opacity-0 data-open:opacity-100",
              )}
            />
          )}
          <NavigationMenu.Positioner
            align={floating ? "start" : align}
            anchor={floating ? undefined : anchor}
            sideOffset={12}
            // Floating dropdowns are portaled into the already-fixed header, so
            // position them absolutely within it rather than as a second fixed
            // layer. A repositioned fixed+GPU layer flashes a stale page tile in
            // the gap under the bar during rapid trigger switches; an in-flow
            // absolute layer (as in the prototype) composites with the header
            // and doesn't.
            positionMethod={floating ? "absolute" : "fixed"}
            className={cn(
              floating ? "w-auto" : "w-[calc(100%-24px)] max-w-[1200px]",
              !floating && dropdownClassName,
            )}
          >
            <NavigationMenu.Popup
              className={cn(
                "mt-1.5 overflow-hidden rounded-lg",
                floating
                  ? cn(
                      navPanelBorder,
                      navFg,
                      navPanelBg,
                      // Keep the fixed popup on a stable, opaque GPU layer so
                      // Chrome doesn't flash a stale/empty tile (the page behind)
                      // when repositioning between triggers.
                      "[backface-visibility:hidden] [transform:translateZ(0)]",
                      // Enter animates in; close is instant (no ending-style) so
                      // there's no window where the fixed popup fades out over the
                      // live page and Chrome composites a stale frame in the gap
                      // above it. Matches the non-floating navbar, which also
                      // closes instantly and never shows this flash.
                      "border-[0.5px] transition-[opacity,translate] duration-200 ease-out data-[starting-style]:-translate-y-1.5 data-[starting-style]:opacity-0",
                    )
                  : "bg-nav-background border-nav-border/30 text-nav-content-primary border",
              )}
            >
              <NavigationMenu.Viewport />
            </NavigationMenu.Popup>
          </NavigationMenu.Positioner>
        </NavigationMenu.Portal>
      </div>
    </NavigationMenu.Root>
  );
}
